import { makeAutoObservable, runInAction } from "mobx";
import { FavoritePostsService } from "../FavoritePosts/FavoritePostsService";
import { IFeedItem, FeedService } from "../Feed/FeedService";
import { IMasterRating, IPublicMaster, PublicMasterProfileService } from "../PublicMasterProfile/PublicMasterProfileService";

export class FeedDetailsStore {
  Item: IFeedItem | null = null;
  SimilarItems: IFeedItem[] = [];
  Master: IPublicMaster | null = null;

  IsLoading = false;
  Error = "";

  IsLoadingSimilar = false;
  IsLoadingMoreSimilar = false;
  SimilarError = "";
  SimilarNextCursor: string | null = null;
  SimilarHasMore = false;
  SimilarLimit = 20;

  IsFavorite = false;
  IsFavoriteLoading = false;
  FavoriteError = "";

  Rating: IMasterRating | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async load(id: number): Promise<void> {
    this.Error = "";
    this.IsLoading = true;
    this.IsFavorite = false;
    this.FavoriteError = "";
    this.Item = null;
    this.Master = null;
    this.SimilarItems = [];
    this.SimilarNextCursor = null;
    this.SimilarHasMore = false;
    this.SimilarError = "";
    this.Rating = null;

    try {
      const item = await FeedService.GetFeedItem(id);

      if (!item) {
        throw new Error("post not found");
      }

      runInAction(() => {
        this.Item = item;
        this.IsFavorite = !!item.isFavorite;

        // ВАЖНО: страницу открываем сразу после получения поста.
        // Главная картинка уже может начать грузиться.
        this.IsLoading = false;
      });

      this.loadMasterAndRating(item.masterId);
      this.loadSimilarLater(id);
    } catch {
      runInAction(() => {
        this.Error = "Не удалось загрузить идею";
        this.IsLoading = false;
      });
    }
  }

  private async loadMasterAndRating(masterId: number) {
    try {
      const [master, rating] = await Promise.all([PublicMasterProfileService.GetMaster(masterId).catch(() => null), PublicMasterProfileService.GetRating(masterId).catch(() => null)]);

      runInAction(() => {
        this.Master = master;
        this.Rating = rating;
      });
    } catch {
      // Не ломаем страницу поста из-за данных мастера
    }
  }

  private loadSimilarLater(postId: number) {
    const run = () => {
      this.loadSimilar(postId);
    };

    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(run, { timeout: 1200 });
      return;
    }

    globalThis.setTimeout(run, 500);
  }

  async loadSimilar(postId?: number) {
    const id = postId ?? this.Item?.id;
    if (!id || this.IsLoadingSimilar) return;

    this.IsLoadingSimilar = true;
    this.SimilarError = "";

    try {
      const response = await FeedService.GetSimilarItems(id, this.SimilarLimit);

      runInAction(() => {
        this.SimilarItems = response.posts ?? [];
        this.SimilarNextCursor = response.nextCursor;
        this.SimilarHasMore = response.hasMore;
      });
    } catch {
      runInAction(() => {
        this.SimilarError = "Не удалось загрузить похожие идеи";
      });
    } finally {
      runInAction(() => {
        this.IsLoadingSimilar = false;
      });
    }
  }

  async loadMoreSimilar() {
    if (!this.Item || !this.SimilarHasMore || this.IsLoadingMoreSimilar) return;

    this.IsLoadingMoreSimilar = true;
    this.SimilarError = "";

    try {
      const response = await FeedService.GetSimilarItems(this.Item.id, this.SimilarLimit, this.SimilarNextCursor);

      runInAction(() => {
        this.SimilarItems = [...this.SimilarItems, ...(response.posts ?? [])];
        this.SimilarNextCursor = response.nextCursor;
        this.SimilarHasMore = response.hasMore;
      });
    } catch {
      runInAction(() => {
        this.SimilarError = "Не удалось загрузить ещё";
      });
    } finally {
      runInAction(() => {
        this.IsLoadingMoreSimilar = false;
      });
    }
  }

  get PriceText() {
    const price = this.Item?.minPrice ?? this.Item?.price;

    if (!price || price <= 0) {
      return "";
    }

    return `${price.toLocaleString("ru-RU")} ₽`;
  }

  async toggleFavorite() {
    if (!this.Item || this.IsFavoriteLoading) return;

    this.IsFavoriteLoading = true;
    this.FavoriteError = "";

    const postId = this.Item.id;
    const nextValue = !this.IsFavorite;

    runInAction(() => {
      this.IsFavorite = nextValue;

      if (this.Item) {
        this.Item = {
          ...this.Item,
          isFavorite: nextValue,
        };
      }
    });

    try {
      const response = nextValue ? await FavoritePostsService.Add(postId) : await FavoritePostsService.Remove(postId);

      if (!response.success) {
        throw new Error("favorite failed");
      }
    } catch {
      runInAction(() => {
        this.IsFavorite = !nextValue;

        if (this.Item) {
          this.Item = {
            ...this.Item,
            isFavorite: !nextValue,
          };
        }

        this.FavoriteError = nextValue ? "Не удалось сохранить идею" : "Не удалось убрать из избранного";
      });
    } finally {
      runInAction(() => {
        this.IsFavoriteLoading = false;
      });
    }
  }
}
